import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import birthdayArt from '../../assets/celebrations/birthday.jpeg';
import anniversaryArt from '../../assets/celebrations/anniversary.jpeg';
import './celebrations.css';

const API = process.env.REACT_APP_BACKEND_URL;
const reducedMotion = () =>
  window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const BALLOON_COLORS = ['#f472b6', '#facc15', '#38bdf8', '#a78bfa', '#34d399', '#fb923c'];

const fireConfetti = async (etype) => {
  if (reducedMotion()) return;
  try {
    const confetti = (await import('canvas-confetti')).default;
    const colors = etype === 'birthday'
      ? ['#f472b6', '#facc15', '#38bdf8', '#a78bfa']
      : ['#eab308', '#0ea5e9', '#14b8a6', '#f59e0b'];
    confetti({ particleCount: 120, spread: 85, origin: { y: 0.62 }, colors, disableForReducedMotion: true });
    setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 60, origin: { x: 0 }, colors }), 350);
    setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 60, origin: { x: 1 }, colors }), 350);
  } catch (e) { /* confetti is decorative — never break the page */ }
};

export const CelebrationCard = ({ event, staticMode }) => {
  const isBday = event.event_type === 'birthday';
  return (
    <div className={`celebration-card ${staticMode ? '' : 'celebration-card-enter'}`}
         data-testid="celebration-card">
      <div className={`celebration-ribbon ${isBday ? 'ribbon-birthday' : 'ribbon-anniversary'}`}>
        {isBday ? 'Happy Birthday!' : 'Work Anniversary'}
      </div>
      <img src={isBday ? birthdayArt : anniversaryArt} alt=""
           className="celebration-art" draggable={false} />
      <h2 className="celebration-name" data-testid="celebration-employee-name">
        {event.employee_name}
      </h2>
      {!isBday && event.years ? (
        <div className="celebration-years" data-testid="celebration-years">
          <span className="years-number">{event.years}</span>
          <span className="years-label">{event.years === 1 ? 'Year' : 'Years'} of Excellence</span>
        </div>
      ) : null}
      <p className="celebration-message" data-testid="celebration-message">{event.message}</p>
    </div>
  );
};

export default function CelebrationOverlay({ demoEvents = null, onDemoClose = null }) {
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  // BDAY-WISH-001 — 'card' | 'compose' | 'sent'
  const [stage, setStage] = useState('card');
  const [wishText, setWishText] = useState('');
  const [sending, setSending] = useState(false);
  const [wishError, setWishError] = useState('');
  const [sentIds, setSentIds] = useState(() => new Set());
  const firedFor = useRef(new Set());
  const lastFetchRef = useRef(0);
  const sessionDone = useRef(false);
  const isDemo = Array.isArray(demoEvents);
  const staticMode = reducedMotion();
  const location = useLocation();

  useEffect(() => {
    if (isDemo) { setEvents(demoEvents); setIdx(0); setOpen(demoEvents.length > 0); return; }
    if (sessionDone.current || open) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const now = Date.now();
    if (now - lastFetchRef.current < 60000) return; // route-change throttle
    lastFetchRef.current = now;
    fetch(`${API}/api/celebrations/today`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!d || !d.animations_enabled) return;
        const unseen = (d.events || []).filter(e => !e.viewed);
        if (unseen.length) { setEvents(unseen); setIdx(0); setOpen(true); }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, demoEvents, location.pathname]);

  useEffect(() => {
    if (!open || !events[idx]) return;
    const ev = events[idx];
    if (!firedFor.current.has(ev.id)) {
      firedFor.current.add(ev.id);
      fireConfetti(ev.event_type);
    }
  }, [open, idx, events]);

  const ack = useCallback((eventId) => {
    if (isDemo) return;
    const token = localStorage.getItem('token');
    fetch(`${API}/api/celebrations/${eventId}/ack`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ dismissed: true }),
    }).catch(() => {});
  }, [isDemo]);

  const close = useCallback(() => {
    events.forEach(e => ack(e.id)); // once-per-user guarantee, survives refresh
    sessionDone.current = true;
    setOpen(false);
    if (onDemoClose) onDemoClose();
  }, [events, ack, onDemoClose]);

  if (!open || !events.length) return null;
  const ev = events[idx];
  const isLast = idx === events.length - 1;
  // ANNIV-WISH-001: wishes for both birthdays and work anniversaries (same architecture)
  const isBdayEv = ev.event_type === 'birthday';
  const canWish = (isBdayEv || ev.event_type === 'anniversary') && ev.id !== 'demo' && !ev.is_self && !ev.wished && !sentIds.has(ev.id);
  const firstName = ev.first_name || (ev.employee_name || '').split(' ').filter(Boolean).pop() || 'there';
  const wishCta = isBdayEv ? 'Send a birthday wish 🎂' : 'Congratulate 🎉';
  const wishRibbon = isBdayEv ? 'Send a birthday wish' : 'Send a work-anniversary wish';

  const openComposer = () => {
    setWishText(isBdayEv
      ? `Happy Birthday, ${firstName}! 🎉`
      : `🎉 Congratulations on your ${ev.years}-year work anniversary, ${firstName}! Wishing you continued success and many more milestones ahead.`);
    setWishError('');
    setStage('compose');
  };

  const afterWish = () => {
    if (isLast) { close(); return; }
    ack(ev.id);
    setStage('card');
    setIdx(i => i + 1);
  };

  const sendWish = async () => {
    const message = wishText.replace(/\s+/g, ' ').trim();
    if (!message) { setWishError('Please write a short wish.'); return; }
    setSending(true); setWishError('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/celebrations/${ev.id}/wishes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ message }),
      });
      if (r.ok || r.status === 409) {
        setSentIds(prev => new Set(prev).add(ev.id));
        setStage('sent');
        fireConfetti(ev.event_type);
      } else {
        const d = await r.json().catch(() => ({}));
        setWishError(typeof d.detail === 'string' ? d.detail : 'Could not send your wish. Please try again.');
      }
    } catch (e) {
      setWishError('Could not send your wish. Please try again.');
    } finally { setSending(false); }
  };

  const renderBody = () => {
    if (stage === 'compose') return (
      <div className="wish-composer celebration-card-enter" data-testid="wish-composer">
        <div className={`celebration-ribbon ${isBdayEv ? 'ribbon-birthday' : 'ribbon-anniversary'}`} data-testid="wish-composer-ribbon">{wishRibbon}</div>
        <h3 className="wish-composer-title" data-testid="wish-composer-title">To {ev.employee_name}</h3>
        <p className="wish-composer-hint">Your name goes with it — make it personal.</p>
        <textarea
          className="wish-textarea"
          data-testid="wish-message-input"
          value={wishText}
          maxLength={280}
          rows={3}
          autoFocus
          onChange={e => setWishText(e.target.value)}
          disabled={sending}
        />
        <div className="wish-meta">
          <span className={`wish-counter ${wishText.length > 250 ? 'wish-counter-warn' : ''}`} data-testid="wish-char-counter">{wishText.length}/280</span>
          {wishError && <span className="wish-error" data-testid="wish-error">{wishError}</span>}
        </div>
        <button className="celebration-dismiss" onClick={sendWish} disabled={sending} data-testid="wish-send-btn">
          {sending ? 'Sending…' : (isBdayEv ? 'Send Wish 🎈' : 'Send Congratulations 🎉')}
        </button>
        <button className="wish-secondary" onClick={() => setStage('card')} disabled={sending} data-testid="wish-back-btn">Back</button>
      </div>
    );
    if (stage === 'sent') return (
      <div className="wish-sent celebration-card-enter" data-testid="wish-sent">
        <div className="wish-sent-emoji" aria-hidden="true">🎈</div>
        <h3 className="celebration-name">Wish sent!</h3>
        <p className="celebration-message">{isBdayEv ? `Your wish is on its way to ${firstName}. Thank you for making their day brighter.` : `Your congratulations are on their way to ${firstName}. Thank you for celebrating their milestone.`}</p>
        <button className="celebration-dismiss" onClick={afterWish} data-testid="wish-done-btn">
          {isLast ? 'Done' : 'Next celebration ›'}
        </button>
      </div>
    );
    return (
      <>
        <CelebrationCard key={ev.id} event={ev} staticMode={staticMode} />
        {events.length > 1 && (
          <div className="celebration-carousel" data-testid="celebration-carousel">
            <button className="carousel-btn" disabled={idx === 0}
                    onClick={() => setIdx(i => i - 1)} data-testid="celebration-prev">‹</button>
            <div className="carousel-dots">
              {events.map((e, i) => (
                <span key={e.id} className={`dot ${i === idx ? 'dot-active' : ''}`}
                      onClick={() => setIdx(i)} />
              ))}
            </div>
            <button className="carousel-btn" disabled={isLast}
                    onClick={() => { ack(ev.id); setIdx(i => i + 1); }}
                    data-testid="celebration-next">›</button>
          </div>
        )}
        {canWish ? (
          <button className="celebration-dismiss" onClick={openComposer} data-testid="celebration-dismiss-btn" data-wish-type={ev.event_type}>
            {wishCta}
          </button>
        ) : (
          <button className="celebration-dismiss" onClick={close} data-testid="celebration-dismiss-btn">
            {!isLast ? 'Skip All' : ev.is_self ? 'Thank you all! 🎉' : (ev.wished || sentIds.has(ev.id)) ? 'Wish sent ✓ — Close' : 'Join the Celebration 🎉'}
          </button>
        )}
      </>
    );
  };

  // Portal to body: ancestors with backdrop-filter/transform (e.g. the HR
  // dashboard widget card) would otherwise trap position:fixed.
  return createPortal(
    <div className="celebration-backdrop" data-testid="celebration-overlay" role="dialog"
         aria-label="Team celebration">
      {!staticMode && (
        <div className="balloon-field" aria-hidden="true">
          {BALLOON_COLORS.map((c, i) => (
            <div key={i} className="balloon" style={{
              '--bcolor': c, left: `${8 + i * 15}%`,
              animationDelay: `${i * 0.7}s`, animationDuration: `${9 + (i % 3) * 2}s`,
            }}><div className="balloon-string" /></div>
          ))}
        </div>
      )}
      <div className="celebration-modal">
        <button className="celebration-close" onClick={close} aria-label="Close celebration"
                data-testid="celebration-close-btn">✕</button>
        {renderBody()}
      </div>
    </div>,
    document.body
  );
}
